import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { DirectUpload } from 'activestorage';

export function useStaffChatAttachments(chatId) {
  const store = useStore();
  const { t } = useI18n();
  const attachedFiles = ref([]);

  const accountId = computed(() => store.getters.getCurrentAccountId);
  const currentUser = computed(() => store.getters.getCurrentUser);
  const globalConfig = computed(() => store.getters['globalConfig/get'] || {});

  const installationLimit = computed(() =>
    resolveMaximumFileUploadSize(globalConfig.value.maximumFileUploadSize)
  );

  const hasAttachments = computed(() => attachedFiles.value.length > 0);
  const hasRecordedAudio = computed(() =>
    attachedFiles.value.some(file => file.isRecordedAudio)
  );

  const alertOverLimit = maxSizeMB => {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
      })
    );
  };

  const attachFile = ({ blob, file }) => {
    const reader = new FileReader();
    reader.readAsDataURL(file.file);
    reader.onloadend = () => {
      attachedFiles.value.push({
        chatId: chatId.value,
        resource: blob || file,
        thumb: reader.result,
        blobSignedId: blob ? blob.signed_id : undefined,
        isRecordedAudio: file?.isRecordedAudio || false,
      });
    };
  };

  const onDirectFileUpload = file => {
    if (!file) return;

    const maxSizeMB = installationLimit.value;

    if (!checkFileSizeLimit(file, maxSizeMB)) {
      alertOverLimit(maxSizeMB);
      return;
    }

    const upload = new DirectUpload(
      file.file,
      `/api/v1/accounts/${accountId.value}/staff_chats/${chatId.value}/direct_uploads`,
      {
        directUploadWillCreateXHR: xhr => {
          xhr.setRequestHeader(
            'api_access_token',
            currentUser.value.access_token
          );
        },
      }
    );

    upload.create((error, blob) => {
      if (error) {
        useAlert(error);
      } else {
        attachFile({ file, blob });
      }
    });
  };

  const onIndirectFileUpload = file => {
    if (!file) return;

    const maxSizeMB = installationLimit.value;

    if (!checkFileSizeLimit(file, maxSizeMB)) {
      alertOverLimit(maxSizeMB);
      return;
    }

    attachFile({ file });
  };

  const onFileUpload = file => {
    if (globalConfig.value.directUploadsEnabled) {
      onDirectFileUpload(file);
    } else {
      onIndirectFileUpload(file);
    }
  };

  const removeAttachment = attachments => {
    attachedFiles.value = attachments;
  };

  const clearAttachments = () => {
    attachedFiles.value = [];
  };

  const getFilesForUpload = () => {
    if (!attachedFiles.value.length) return [];

    return attachedFiles.value.map(attachment => {
      if (globalConfig.value.directUploadsEnabled) {
        return attachment.blobSignedId;
      }
      return attachment.resource.file;
    });
  };

  const onCannedAttachment = async fileData => {
    if (!fileData) return;

    // Reuse the already-uploaded blob when direct uploads are on, so we never
    // re-fetch the (possibly cross-origin/S3) file URL.
    if (globalConfig.value.directUploadsEnabled && fileData.blob_signed_id) {
      attachedFiles.value.push({
        chatId: chatId.value,
        resource: { file: fileData.blob_signed_id },
        thumb: fileData.file_url,
        blobSignedId: fileData.blob_signed_id,
      });
      return;
    }

    try {
      const response = await fetch(fileData.file_url);
      const blob = await response.blob();
      const file = new File([blob], fileData.filename || 'image.png', {
        type: fileData.file_type || blob.type,
      });
      onFileUpload({ file, name: file.name, type: file.type, size: file.size });
    } catch {
      useAlert(t('CANNED_MGMT.FORM.IMAGE.ATTACH_FAILED'));
    }
  };

  return {
    attachedFiles,
    hasAttachments,
    hasRecordedAudio,
    onFileUpload,
    onCannedAttachment,
    removeAttachment,
    clearAttachments,
    getFilesForUpload,
  };
}
